import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { InputRef } from 'antd';
import { GetGatewaysList } from 'core/domain/gateways/repositories/getGatewaysList';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { GatewayAssetModel, GatewaysListDataModel } from 'core/domain/gateways/model';
import { useMessage } from 'hooks/useMessage';
import { GatewaysListColumns, GatewaysListColumnsTranslations } from './GatewaysListColumns';
import { getProjectPathById, getSharedGatewayPathByGatewayId, SharedGatewayTabType } from 'components/pages/App/routes/projects/config';
import { getAssetPathById, getAssetWithParamsPathById } from 'components/pages/App/routes/assets/config';
import { GatewaysOptions, CustomGatewaysListDataModel } from './utils';
import { useAssignToAsset } from './useAssignToAsset';
import { useUnAssignToAsset } from './useUnAssignToAsset';
import { useAssignToProject } from './useAssignToProject';
import { useUnAssignToProject } from './useUnAssignToProject';
import { getGatewayDetailPath, getGatewaysListWithPaginationPath } from 'components/pages/App/routes/gateways/config';
import {
  TABLE_PAGE_SIZE_DEFAULT_OPTIONS,
  TABLE_PAGINATION_PAGE_DEFAULT,
  TABLE_PAGINATION_SIZE_DEFAULT,
  TablePaginationSearchValue,
} from 'constants/table';
import { useDeleteGateway } from './useDeleteGateway';
import { AssetTabType } from 'components/pages/AssetPage';
import { GatewayDetailTabType } from 'components/pages/GatewayDetailPage/utils';

const TYPE_PRIVATE = 'private';

export const useGatewaysList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const { setMessageError } = useMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [gateways, setGateways] = useState<CustomGatewaysListDataModel[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [pageSize, setPageSize] = useState<number>(Number(TABLE_PAGINATION_SIZE_DEFAULT));
  const [totalPages, setTotalPages] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const page = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.PAGE);
  const size = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SIZE);
  const {
    clientsOptions: clientsAssetsOptions,
    projectsOptions: projectsAssetsOptions,
    assetsOptions,
    assignToAssetModalOptions,
    onOpenAssignToAssetModal,
    successAssigningGateway,
    assigningToAsset,
  } = useAssignToAsset();
  const {
    successUnAssigningGateway: successAssigningGatewayToAsset,
    onOpenUnassignToAssetModal,
    unAssigningToAssetModalOptions,
  } = useUnAssignToAsset();
  const {
    assignToProjectModalOptions,
    assigningToProject,
    clientsOptions: clientsProjectOptions,
    projectsOptions: projectsProjectsOptions,
    onOpenAssignToProjectModal,
    successAssigningGatewayToProject,
  } = useAssignToProject();
  const {
    onOpenUnAssignToProjectModal,
    successUnAssigningGateway: successUnAssigningGatewayToProject,
    unAssigningToProjectModalOptions,
  } = useUnAssignToProject();
  const { onOpenDeleteGatewayModal, deleteGatewayOptions, successDeletingGateway } = useDeleteGateway();

  const inputRef = useRef<InputRef | null>(null);

  const title: string = t('_GATEWAYS_LIST_TITLE');

  const searchButtonText: string = t('search_with_ellipsis');

  const gatewayListColumnsTranslations: GatewaysListColumnsTranslations = {
    actionsTitle: t('actions'),
    assetAliasText: t('asset'),
    projectAliasText: t('project'),
    typeTitle: t('type'),
    privateTypeText: t('_GATEWAYS_LIST_COLUMNS_TYPE_PRIVATE'),
    publicTypeText: t('_GATEWAYS_LIST_COLUMNS_TYPE_PUBLIC'),
    notReportedText: t('not_reported'),
    notAssignedText: t('_GATEWAYS_LIST_NOT_ASSIGNED'),
    assignGatewayToAssetText: t('_GATEWAYS_LIST_ASSIGN_TO_ASSET'),
    unassignGatewayToAssetText: t('_GATEWAYS_LIST_UNASSIGN_TO_ASSET'),
    assignGatewayToProjectText: t('_GATEWAYS_LIST_ASSIGN_TO_PROJECT'),
    unassignGatewayToProjectText: t('_GATEWAYS_LIST_UNASSIGN_TO_PROJECT'),
    deleteGatewayButtonText: t('_GATEWAYS_LIST_DELETE_BUTTON_TEXT'),
    viewDetailGatewayButtonText: t('_GATEWAYS_LIST_VIEW_DETAIL_BUTTON_TEXT'),
  };

  const onSelectProject = (projectId: string) => {
    const projectRoute = getProjectPathById({ projectId });
    history.push(projectRoute);
  };

  const onSelectAsset = (assetId: string) => {
    const assetRoute = getAssetPathById(assetId);
    history.push(assetRoute);
  };

  const navigateToSharedGateway = (id: string, projectId: string) => {
    history.push(getSharedGatewayPathByGatewayId({ projectId, gatewayId: id, tab: SharedGatewayTabType.INFO }));
  };

  const navigateToPrivateGateway = (id: string, assets: GatewayAssetModel[]) => {
    const hasLinkedAssets = !!assets.length;
    hasLinkedAssets
      ? history.push(getAssetWithParamsPathById({ assetId: assets[0].id, tab: AssetTabType.GATEWAY }))
      : history.push(getGatewayDetailPath({ gatewayId: id, tabId: GatewayDetailTabType.INFO }));
  };

  const onClickViewGatewayDetail = (selectedGateway: CustomGatewaysListDataModel) => {
    const { assets, type, projectId, id } = selectedGateway;
    const isSharedGateway = type === 'shared';
    isSharedGateway ? navigateToSharedGateway(id, projectId || '') : navigateToPrivateGateway(id, assets);
  };

  const columns = GatewaysListColumns({
    translations: gatewayListColumnsTranslations,
    onSelectProject,
    onSelectAsset,
    onOpenAssignToAssetModal,
    onOpenUnassignToAssetModal,
    onOpenAssignToProjectModal,
    onOpenUnAssignToProjectModal,
    onOpenDeleteGatewayModal,
    onClickViewGatewayDetail,
  });

  const onChangePage = (page: number, size?: number) => {
    setCurrentPage(page);
    setPageSize(size!);
    const filteredSize = String(!!size ? size : pageSize);
    history.push(getGatewaysListWithPaginationPath({ page: String(page), size: filteredSize }));
  };

  const payloadToCustomGatewayData = (gatewayData: GatewaysListDataModel): CustomGatewaysListDataModel => {
    const hasAssetsAssignedToGateway = !!gatewayData.assets.length;
    const isProjectAssignedToGateway = !!gatewayData.projectId;
    const isGatewayPrivate = gatewayData.type === TYPE_PRIVATE;

    return {
      ...gatewayData,
      actions: {
        isAssetActionsDisabled: !isGatewayPrivate,
        isAssignedToAsset: isGatewayPrivate && hasAssetsAssignedToGateway,
        isProjectActionsDisabled: hasAssetsAssignedToGateway,
        isAssignedToProject: !isGatewayPrivate && isProjectAssignedToGateway,
      },
    };
  };

  const transformGatewaysToCustomGateways = (gatewaysData: GatewaysListDataModel[]): CustomGatewaysListDataModel[] => {
    return gatewaysData.map((gatewayData) => payloadToCustomGatewayData(gatewayData));
  };

  const getGatewaysList = async ({ page, size, search }: { page: number; size: number; search?: string }) => {
    setLoading(true);
    try {
      const {
        data,
        meta: { totalPages },
      } = await GetGatewaysList({ host, token, pageNumber: page, pageSize: size, search });
      const transformedGateways = transformGatewaysToCustomGateways(data);
      setTotalPages(totalPages);
      setGateways(transformedGateways);
    } catch (error) {
      setMessageError({ description: t('_GATEWAYS_LIST_ERROR_MESSAGE') });
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (value: string) => {
    setSearchQuery(value);
    setSearchValue(value);
    setCurrentPage(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  };

  const onUpdateSearchValue = (newValue: string) => {
    setSearchValue(newValue);
  };

  useEffect(() => {
    const parsedPage = page || TABLE_PAGINATION_PAGE_DEFAULT;
    const parsedSize = size || TABLE_PAGINATION_SIZE_DEFAULT;
    setCurrentPage(Number(parsedPage));
    setPageSize(Number(parsedSize));
    getGatewaysList({ page: Number(parsedPage), size: Number(parsedSize), search: searchQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchQuery]);

  const refreshPageAndSize = () => {
    const parsedPage = page || TABLE_PAGINATION_PAGE_DEFAULT;
    const parsedSize = size || TABLE_PAGINATION_SIZE_DEFAULT;
    setCurrentPage(Number(parsedPage));
    setPageSize(Number(parsedSize));
    getGatewaysList({ page: Number(parsedPage), size: Number(parsedSize) });
  };

  const refreshSearchQuery = () => {
    setSearchValue('');
    setSearchQuery('');
  };

  useEffect(() => {
    if (
      !!successAssigningGateway ||
      !!successAssigningGatewayToAsset ||
      !!successAssigningGatewayToProject ||
      !!successUnAssigningGatewayToProject ||
      !!successDeletingGateway
    ) {
      !!searchQuery ? refreshSearchQuery() : refreshPageAndSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    successAssigningGateway,
    successAssigningGatewayToAsset,
    successAssigningGatewayToProject,
    successUnAssigningGatewayToProject,
    successDeletingGateway,
  ]);

  const gatewaysOptions: GatewaysOptions = {
    gateways,
    columns,
    totalPages,
    currentPage,
    pageSize,
    searchButtonText,
    defaultPageSize: TABLE_PAGINATION_SIZE_DEFAULT,
    pageSizeOptions: TABLE_PAGE_SIZE_DEFAULT_OPTIONS,
    inputRef,
    searchValue,
    onChangePage,
    onSearch,
    onUpdateSearchValue,
    loading,
  };

  return {
    title,
    assetsOptions,
    clientsAssetsOptions,
    clientsProjectOptions,
    projectsAssetsOptions,
    projectsProjectsOptions,
    gatewaysOptions,
    assignToAssetModalOptions,
    assignToProjectModalOptions,
    unAssigningToAssetModalOptions,
    unAssigningToProjectModalOptions,
    deleteGatewayOptions,
    assigningToAsset,
    assigningToProject,
  };
};
